import { ChevronsDownUp, ChevronsUpDown, Download, RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { MultiSelect } from '~/components/ui/multi-select';
import { getImpactBadgeClass } from '~/constants/impact';
import { cn } from '~/utils/cn';

interface AttributeFilterCardProps {
  /** Impact values present in the current optimization list, in display order. */
  impactOptions: string[];
  /** Currently selected impact values (controlled). */
  selectedImpacts: string[];
  onImpactsChange: (impacts: string[]) => void;
  /** Every enriched attribute name on the product, in display order. */
  attributeOptions: string[];
  /** Currently selected attribute names (controlled). Empty = show all. */
  selectedAttributes: string[];
  onAttributesChange: (attributes: string[]) => void;
  /** True while at least one visible row is expanded — picks the toggle's label
   *  and icon. Omit (with `onToggleAllRows`) to hide the control. */
  anyRowExpanded?: boolean;
  /** Expands every visible row, or collapses them all when `anyRowExpanded`. */
  onToggleAllRows?: () => void;
  /** Re-fetches the enrichment payload backing the attribute rows. Omit to hide
   *  the control — it only makes sense where the parent owns that query. */
  onRefresh?: () => void;
  /** Bound to the enrichment query's fetching state so the button spins. */
  isRefreshing?: boolean;
  /** Exports the currently-visible attributes as JSON. `scope` picks which side
   *  of the comparison is included. Omit to hide the control. */
  onDownload?: (scope: AttributeExportScope) => void;
  /** Enrichments | Language diff switcher. Pass both (only when the product has translations)
   *  to show it; the filter controls apply to Enrichments and hide on Language diff. */
  view?: AttributeCardView;
  onViewChange?: (view: AttributeCardView) => void;
}

/** What the card's body shows: the enriched attributes, or English vs each translation. */
export type AttributeCardView = 'enrichments' | 'languages';

/** Which side(s) of the before/after comparison an export includes. */
export type AttributeExportScope = 'pre' | 'post' | 'both';

/**
 * Filter bar shown above the optimization accordion — two combo boxes that
 * narrow `EnrichedAttributeRow`s by impact severity and by attribute name. The
 * attribute combo box lists every enriched attribute on the product, so the set
 * is discoverable instead of having to be typed from memory; both carry their
 * own search input for long lists. Also hosts the expand/collapse-all toggle for
 * the rows below, so bulk open/shut sits with the other list-wide controls.
 */
export function AttributeFilterCard({
  impactOptions,
  selectedImpacts,
  onImpactsChange,
  attributeOptions,
  selectedAttributes,
  onAttributesChange,
  anyRowExpanded = false,
  onToggleAllRows,
  onRefresh,
  isRefreshing = false,
  onDownload,
  view,
  onViewChange,
}: AttributeFilterCardProps) {
  const { t } = useTranslation('catalog-operations');
  const hasActiveFilters = selectedImpacts.length > 0 || selectedAttributes.length > 0;
  const showTabs = view !== undefined && onViewChange !== undefined;
  const onLanguages = showTabs && view === 'languages';

  return (
    <div
      data-testid="products-attribute-filter-card"
      className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div>
          <h5
            data-testid="products-attribute-filter-title"
            className="text-foreground text-lg font-medium"
          >
            {t('attributeFilter.heading')}
          </h5>
          <p className="text-muted-foreground text-sm">
            {onLanguages ? t('attributeFilter.languagesSubtext') : t('attributeFilter.subtext')}
          </p>
        </div>
        {showTabs && (
          <SegmentedTabs
            value={view}
            onChange={onViewChange}
            ariaLabel={t('attributeFilter.viewTabsLabel')}
            testId="products-attribute-filter-view-tabs"
            // On a card surface: bg-background container with a border (CLAUDE.md tab rule).
            className="bg-background w-auto border"
            options={[
              { value: 'enrichments', label: t('attributeFilter.tabEnrichments') },
              { value: 'languages', label: t('attributeFilter.tabLanguageDiff') },
            ]}
          />
        )}
      </div>
      {/* Filters, bulk toggle, export and refresh act on the enriched rows only. */}
      <div
        data-testid="products-attribute-filter-controls"
        className={cn('flex flex-row gap-3 sm:flex-row sm:items-center', onLanguages && 'hidden')}
      >
        {/* First in the control row — bulk open/shut reads before the filters
            that decide which rows it applies to. */}
        {onToggleAllRows ? (
          <Button
            data-testid="products-attribute-filter-toggle-all-button"
            variant="ghost"
            size="sm"
            onClick={onToggleAllRows}
            className="shrink-0 cursor-pointer gap-1.5"
          >
            {anyRowExpanded ? (
              <ChevronsDownUp className="size-4" />
            ) : (
              <ChevronsUpDown className="size-4" />
            )}
            {anyRowExpanded ? t('attributeFilter.collapseAll') : t('attributeFilter.expandAll')}
          </Button>
        ) : null}
        {/* Hidden when the payload carries no impact scoring — the sample
            preview doesn't — since an empty combo box is a control that can only
            disappoint. */}
        {impactOptions.length > 0 && (
          <MultiSelect
            options={impactOptions.map((impact) => ({
              value: impact,
              label: impact,
              // Mirrors the impact badge shown on each EnrichedAttributeRow (rounded-sm, medium weight, severity colour).
              badgeClassName: cn('rounded-sm font-medium', getImpactBadgeClass(impact)),
            }))}
            value={selectedImpacts}
            onChange={onImpactsChange}
            placeholder={t('attributeFilter.impactPlaceholder')}
            searchPlaceholder={t('attributeFilter.impactSearchPlaceholder')}
            className="min-w-44 sm:max-w-48"
            testId="products-attribute-filter-impact-select"
          />
        )}
        <MultiSelect
          options={attributeOptions.map((attribute) => ({ value: attribute, label: attribute }))}
          value={selectedAttributes}
          onChange={onAttributesChange}
          placeholder={t('attributeFilter.attributePlaceholder')}
          searchPlaceholder={t('attributeFilter.attributeSearchPlaceholder')}
          className="min-w-56 sm:max-w-64"
          testId="products-attribute-filter-attribute-select"
        />
        {hasActiveFilters ? (
          <Button
            data-testid="products-attribute-filter-clear-all-button"
            variant="ghost"
            size="sm"
            onClick={() => {
              onImpactsChange([]);
              onAttributesChange([]);
            }}
            className="cursor-pointer gap-1.5"
          >
            <X className="size-4" />
            {t('attributeFilter.clearAll')}
          </Button>
        ) : null}
        {onDownload ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="products-attribute-filter-download-button"
                variant="outline"
                size="icon"
                aria-label={t('attributeFilter.download.trigger')}
                title={t('attributeFilter.download.trigger')}
                className="shrink-0 cursor-pointer"
              >
                <Download className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent data-testid="products-attribute-filter-download-menu" align="end">
              <DropdownMenuLabel>{t('attributeFilter.download.heading')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="products-attribute-filter-download-both-item"
                className="cursor-pointer"
                onClick={() => {
                  onDownload('both');
                }}
              >
                {t('attributeFilter.download.both')}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="products-attribute-filter-download-pre-item"
                className="cursor-pointer"
                onClick={() => {
                  onDownload('pre');
                }}
              >
                {t('attributeFilter.download.pre')}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="products-attribute-filter-download-post-item"
                className="cursor-pointer"
                onClick={() => {
                  onDownload('post');
                }}
              >
                {t('attributeFilter.download.post')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        {onRefresh ? (
          <Button
            data-testid="products-attribute-filter-refresh-button"
            variant="outline"
            size="icon"
            onClick={onRefresh}
            isLoading={isRefreshing}
            aria-label={t('attributeFilter.refresh')}
            title={t('attributeFilter.refresh')}
            className={cn('shrink-0', isRefreshing ? 'cursor-not-allowed' : 'cursor-pointer')}
          >
            {isRefreshing ? null : <RefreshCw className="size-4" />}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
